import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, message, Modal, Form, Input, InputNumber,
  Switch, DatePicker, Space, Tag, Popconfirm, Tooltip, Badge,
  Descriptions, Drawer, Divider, Select, Typography, Empty, Tabs, Alert
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  SearchOutlined, GiftOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CreditCardOutlined, CrownOutlined, StarOutlined
} from '@ant-design/icons';
import { apiClient } from '@/services/api';
import dayjs from 'dayjs';
import '@/styles/global.css';

const { Text } = Typography;
const { Option } = Select;

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface Coupon {
  id: string;
  code: string;
  name: string;
  credits: number;
  description?: string;
  validFrom: string;
  validTo: string;
  maxUses?: number;
  currentUses: number;
  isActive: boolean;
  onlyForNewUsers: boolean;
  createdAt: string;
  usages?: Array<{
    id: string;
    userId: string;
    usedAt: string;
    user?: {
      id: string;
      username?: string;
      email?: string;
      phone?: string;
    };
  }>;
}

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  bonus: number;
  popular: boolean;
  enabled: boolean;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  credits: number;
  period: 'monthly' | 'yearly';
  features: string[];
  enabled: boolean;
  popular?: boolean;
}

const CouponManagement: React.FC = () => {
  // 优惠券相关状态
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [usageStatus, setUsageStatus] = useState<string>('all');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [batchResult, setBatchResult] = useState<any>(null);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();

  // 积分包和订阅计划相关状态
  const [editPackageModal, setEditPackageModal] = useState(false);
  const [editPlanModal, setEditPlanModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [packageForm] = Form.useForm();
  const [planForm] = Form.useForm();

  // 默认积分包数据
  const defaultPackages: CreditPackage[] = [
    { id: 'pkg_1', name: '入门包', credits: 100, price: 9.99, bonus: 0, popular: false, enabled: true },
    { id: 'pkg_2', name: '标准包', credits: 500, price: 39.99, bonus: 50, popular: true, enabled: true },
    { id: 'pkg_3', name: '专业包', credits: 1000, price: 69.99, bonus: 150, popular: false, enabled: true },
    { id: 'pkg_4', name: '旗舰包', credits: 3000, price: 179.99, bonus: 500, popular: false, enabled: true },
  ];

  // 默认订阅计划数据
  const defaultPlans: SubscriptionPlan[] = [
    {
      id: 'plan_1',
      name: '基础会员',
      price: 19.99,
      credits: 200,
      period: 'monthly',
      features: ['每月200积分', '优先支持', '无广告'],
      enabled: true,
      popular: false
    },
    {
      id: 'plan_2',
      name: '高级会员',
      price: 49.99,
      credits: 600,
      period: 'monthly',
      features: ['每月600积分', 'VIP支持', '无广告', '全部功能'],
      enabled: true,
      popular: true
    },
    {
      id: 'plan_3',
      name: '专业会员',
      price: 99.99,
      credits: 1500,
      period: 'monthly',
      features: ['每月1500积分', 'VIP支持', '无广告', '全部功能', '专属客服'],
      enabled: true,
      popular: false
    },
    {
      id: 'plan_4',
      name: '年度会员',
      price: 499.99,
      credits: 8000,
      period: 'yearly',
      features: ['全年8000积分', 'VIP支持', '无广告', '全部功能', '专属客服'],
      enabled: true,
      popular: false
    },
  ];

  // 加载优惠券列表
  const loadCoupons = async (page = 1, search = '', usageStatusFilter = 'all', activeFilter = 'all') => {
    setLoading(true);
    try {
      const params: any = { page, limit: pagination.limit };
      if (search) params.search = search;
      if (usageStatusFilter !== 'all') params.usageStatus = usageStatusFilter;
      if (activeFilter !== 'all') params.isActive = activeFilter;

      const response = await apiClient.get('/admin/coupons', { params });
      
      if (response.success) {
        setCoupons(response.data.coupons);
        setPagination({
          ...pagination,
          page: response.data.pagination.page,
          total: response.data.pagination.total
        });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons(1, searchText, usageStatus, isActiveFilter);
  }, [usageStatus, isActiveFilter]);

  // 加载积分包和订阅计划数据
  useEffect(() => {
    const loadPaymentData = async () => {
      try {
        // 加载积分包数据
        const packagesResp = await apiClient.get('/admin/payment/packages');
        if (packagesResp?.success) {
          const packagesData = packagesResp.data?.packages || [];
          if (packagesData.length > 0) {
            const formattedPackages = packagesData.map((pkg: any) => ({
              id: pkg.id || '',
              name: pkg.name || '',
              credits: Number(pkg.credits) || 0,
              price: Number(pkg.price) || 0,
              bonus: Number(pkg.bonus) || 0,
              popular: Boolean(pkg.popular),
              enabled: Boolean(pkg.enabled)
            }));
            setPackages(formattedPackages);
          } else {
            setPackages(defaultPackages);
          }
        } else {
          setPackages(defaultPackages);
        }

        // 加载订阅计划数据
        const plansResp = await apiClient.get('/admin/payment/plans');
        if (plansResp?.success) {
          const plansData = plansResp.data?.plans || [];
          if (plansData.length > 0) {
            const formattedPlans = plansData.map((plan: any) => ({
              id: plan.id || '',
              name: plan.name || '',
              price: Number(plan.price) || 0,
              credits: Number(plan.credits) || 0,
              period: plan.period || 'monthly',
              features: Array.isArray(plan.features) ? plan.features : [],
              enabled: Boolean(plan.enabled),
              popular: Boolean(plan.popular)
            }));
            setPlans(formattedPlans);
          } else {
            setPlans(defaultPlans);
          }
        } else {
          setPlans(defaultPlans);
        }
      } catch (error) {
        console.warn('加载积分包和订阅计划数据失败:', error);
        setPackages(defaultPackages);
        setPlans(defaultPlans);
      }
    };

    loadPaymentData();
  }, []);

  // 处理表格变化
  const handleTableChange = (page: number) => {
    loadCoupons(page, searchText, usageStatus, isActiveFilter);
  };

  // 搜索
  const handleSearch = () => {
    loadCoupons(1, searchText, usageStatus, isActiveFilter);
  };

  // 批量创建
  const handleBatchCreate = async () => {
    try {
      const values = await batchForm.validateFields();
      const data = {
        batch: true,
        count: values.count,
        codePrefix: values.codePrefix || '',
        codeLength: values.codeLength || 8,
        name: values.name,
        credits: values.credits,
        description: values.description || '',
        validFrom: values.validFrom.toISOString(),
        validTo: values.validTo.toISOString(),
        maxUses: values.maxUses || null,
        isActive: values.isActive !== undefined ? values.isActive : true,
        onlyForNewUsers: values.onlyForNewUsers !== undefined ? values.onlyForNewUsers : false
      };

      const response = await apiClient.post('/admin/coupons', data);
      if (response.success) {
        setBatchResult(response.data);
        message.success(response.message || '批量创建成功');
        // 不自动关闭，让用户查看结果和复制代码
        loadCoupons(1, searchText, usageStatus, isActiveFilter);
      }
    } catch (error: any) {
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error(error.response?.data?.message || '批量创建失败');
    }
  };

  // 打开创建/编辑模态框
  const handleOpenModal = (coupon?: Coupon) => {
    if (coupon) {
      setEditingCoupon(coupon);
      form.setFieldsValue({
        ...coupon,
        validFrom: dayjs(coupon.validFrom),
        validTo: dayjs(coupon.validTo)
      });
    } else {
      setEditingCoupon(null);
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
        onlyForNewUsers: false,
        validFrom: dayjs(),
        validTo: dayjs().add(1, 'year')
      });
    }
    setModalVisible(true);
  };

  // 保存优惠券
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        validFrom: values.validFrom.toISOString(),
        validTo: values.validTo.toISOString(),
        maxUses: values.maxUses || null
      };

      if (editingCoupon) {
        // 更新
        await apiClient.put(`/admin/coupons/${editingCoupon.id}`, data);
        message.success('优惠券更新成功');
      } else {
        // 创建
        await apiClient.post('/admin/coupons', data);
        message.success('优惠券创建成功');
      }
      
      setModalVisible(false);
      form.resetFields();
      loadCoupons(pagination.page, searchText);
    } catch (error: any) {
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error(error.response?.data?.message || '保存失败');
    }
  };

  // 删除优惠券
  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/admin/coupons/${id}`);
      message.success('删除成功');
      loadCoupons(pagination.page, searchText);
    } catch (error: any) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 查看详情
  const handleViewDetails = async (coupon: Coupon) => {
    try {
      const response = await apiClient.get(`/admin/coupons/${coupon.id}`);
      if (response.success) {
        setSelectedCoupon(response.data.coupon);
        setDrawerVisible(true);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '加载详情失败');
    }
  };

  // 积分包表格列
  const packageColumns = [
    {
      title: '包名',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: CreditPackage) => (
        <Space>
          <span className="font-semibold">{name}</span>
          {record.popular && <Tag color="red" icon={<StarOutlined />}>热门推荐</Tag>}
        </Space>
      ),
    },
    {
      title: '积分数量',
      dataIndex: 'credits',
      key: 'credits',
      render: (credits: number, record: CreditPackage) => (
        <Space>
          <Tag color="blue">{credits} 积分</Tag>
          {record.bonus > 0 && <Tag color="green">+{record.bonus} 赠送</Tag>}
        </Space>
      ),
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => <span className="text-green-600 font-bold">￥{price}</span>,
    },
    {
      title: '性价比',
      key: 'value',
      render: (_: any, record: CreditPackage) => {
        const totalCredits = record.credits + record.bonus;
        const ratio = (totalCredits / record.price).toFixed(2);
        return <span className="text-gray-600">{ratio} 积分/￥</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: CreditPackage) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditPackage(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeletePackage(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 订阅计划表格列
  const planColumns = [
    {
      title: '计划名',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: SubscriptionPlan) => (
        <Space>
          <span className="font-semibold">{name}</span>
          {record.popular && <Tag color="purple" icon={<CrownOutlined />}>推荐</Tag>}
        </Space>
      ),
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: SubscriptionPlan) => (
        <div>
          <div className="text-green-600 font-bold">￥{price}</div>
          <div className="text-xs text-gray-500">
            {record.period === 'monthly' ? '每月' : '每年'}
          </div>
        </div>
      ),
    },
    {
      title: '积分',
      dataIndex: 'credits',
      key: 'credits',
      render: (credits: number, record: SubscriptionPlan) => (
        <Tag color="blue">
          {credits} / {record.period === 'monthly' ? '月' : '年'}
        </Tag>
      ),
    },
    {
      title: '功能特权',
      dataIndex: 'features',
      key: 'features',
      render: (features: string[]) => (
        <Space size={[0, 4]} wrap>
          {features.slice(0, 2).map((f, i) => (
            <Tag key={i} color="purple">{f}</Tag>
          ))}
          {features.length > 2 && <Tag>+{features.length - 2}</Tag>}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: SubscriptionPlan) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditPlan(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeletePlan(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 编辑积分包
  const handleEditPackage = (pkg: CreditPackage) => {
    setSelectedPackage(pkg);
    packageForm.setFieldsValue(pkg);
    setEditPackageModal(true);
  };

  // 编辑订阅计划
  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    planForm.setFieldsValue({
      ...plan,
      features: plan.features.join('\n')
    });
    setEditPlanModal(true);
  };

  // 删除积分包
  const handleDeletePackage = async (pkg: CreditPackage) => {
    try {
      Modal.confirm({
        title: '确认删除',
        content: `确定要删除积分包"${pkg.name}"吗？此操作不可撤销。`,
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          try {
            setLoading(true);
            const resp = await apiClient.delete(`/admin/payment/packages/${pkg.id}`);
            
            if (!resp?.success) throw new Error(resp?.message || '删除失败');
            
            message.success('积分包删除成功');
            
            // 重新加载数据
            const packagesResp = await apiClient.get('/admin/payment/packages');
            if (packagesResp?.success) {
              const packagesData = packagesResp.data?.packages || [];
              if (packagesData.length > 0) {
                const formattedPackages = packagesData.map((p: any) => ({
                  id: p.id || '',
                  name: p.name || '',
                  credits: Number(p.credits) || 0,
                  price: Number(p.price) || 0,
                  bonus: Number(p.bonus) || 0,
                  popular: Boolean(p.popular),
                  enabled: Boolean(p.enabled)
                }));
                setPackages(formattedPackages);
              } else {
                setPackages(defaultPackages);
              }
            }
          } catch (error: any) {
            console.error('删除积分包失败:', error);
            message.error('删除失败: ' + (error?.message || '未知错误'));
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error: any) {
      console.error('删除积分包失败:', error);
      message.error('删除失败: ' + (error?.message || '未知错误'));
    }
  };

  // 删除订阅计划
  const handleDeletePlan = async (plan: SubscriptionPlan) => {
    try {
      Modal.confirm({
        title: '确认删除',
        content: `确定要删除订阅计划"${plan.name}"吗？此操作不可撤销。`,
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          try {
            setLoading(true);
            const resp = await apiClient.delete(`/admin/payment/plans/${plan.id}`);
            
            if (!resp?.success) throw new Error(resp?.message || '删除失败');
            
            message.success('订阅计划删除成功');
            
            // 重新加载数据
            const plansResp = await apiClient.get('/admin/payment/plans');
            if (plansResp?.success) {
              const plansData = plansResp.data?.plans || [];
              if (plansData.length > 0) {
                const formattedPlans = plansData.map((p: any) => ({
                  id: p.id || '',
                  name: p.name || '',
                  price: Number(p.price) || 0,
                  credits: Number(p.credits) || 0,
                  period: p.period || 'monthly',
                  features: Array.isArray(p.features) ? p.features : [],
                  enabled: Boolean(p.enabled),
                  popular: Boolean(p.popular)
                }));
                setPlans(formattedPlans);
              } else {
                setPlans(defaultPlans);
              }
            }
          } catch (error: any) {
            console.error('删除订阅计划失败:', error);
            message.error('删除失败: ' + (error?.message || '未知错误'));
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error: any) {
      console.error('删除订阅计划失败:', error);
      message.error('删除失败: ' + (error?.message || '未知错误'));
    }
  };

  // 保存积分包
  const handleSavePackage = async (values: any) => {
    try {
      setLoading(true);
      
      const endpoint = selectedPackage 
        ? `/admin/payment/packages/${selectedPackage.id}` 
        : '/admin/payment/packages';
      const method = selectedPackage ? 'put' : 'post';
      
      const resp = await apiClient[method](endpoint, {
        ...values,
        id: selectedPackage?.id || Date.now().toString()
      });
      
      if (!resp?.success) throw new Error(resp?.message || '保存失败');
      
      message.success(selectedPackage ? '积分包更新成功' : '积分包添加成功');
      setEditPackageModal(false);
      
      // 重新加载积分包数据
      const packagesResp = await apiClient.get('/admin/payment/packages');
      if (packagesResp?.success) {
        const packagesData = packagesResp.data?.packages || [];
        if (packagesData.length > 0) {
          const formattedPackages = packagesData.map((pkg: any) => ({
            id: pkg.id || '',
            name: pkg.name || '',
            credits: Number(pkg.credits) || 0,
            price: Number(pkg.price) || 0,
            bonus: Number(pkg.bonus) || 0,
            popular: Boolean(pkg.popular),
            enabled: Boolean(pkg.enabled)
          }));
          setPackages(formattedPackages);
        } else {
          setPackages(defaultPackages);
        }
      }
    } catch (error: any) {
      console.error('保存积分包失败:', error);
      message.error('保存失败: ' + (error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 保存订阅计划
  const handleSavePlan = async (values: any) => {
    try {
      setLoading(true);
      const planData = {
        ...values,
        features: values.features.split('\n').filter((f: string) => f.trim())
      };
      
      const endpoint = selectedPlan 
        ? `/admin/payment/plans/${selectedPlan.id}` 
        : '/admin/payment/plans';
      const method = selectedPlan ? 'put' : 'post';
      
      const resp = await apiClient[method](endpoint, {
        ...planData,
        id: selectedPlan?.id || Date.now().toString()
      });
      
      if (!resp?.success) throw new Error(resp?.message || '保存失败');
      
      message.success(selectedPlan ? '订阅计划更新成功' : '订阅计划添加成功');
      setEditPlanModal(false);
      
      // 重新加载订阅计划数据
      const plansResp = await apiClient.get('/admin/payment/plans');
      if (plansResp?.success) {
        const plansData = plansResp.data?.plans || [];
        if (plansData.length > 0) {
          const formattedPlans = plansData.map((plan: any) => ({
            id: plan.id || '',
            name: plan.name || '',
            price: Number(plan.price) || 0,
            credits: Number(plan.credits) || 0,
            period: plan.period || 'monthly',
            features: Array.isArray(plan.features) ? plan.features : [],
            enabled: Boolean(plan.enabled),
            popular: Boolean(plan.popular)
          }));
          setPlans(formattedPlans);
        } else {
          setPlans(defaultPlans);
        }
      }
    } catch (error: any) {
      console.error('保存订阅计划失败:', error);
      message.error('保存失败: ' + (error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 判断优惠券状态
  const getCouponStatus = (coupon: Coupon) => {
    const now = new Date();
    const validFrom = new Date(coupon.validFrom);
    const validTo = new Date(coupon.validTo);

    if (!coupon.isActive) {
      return { text: '已停用', color: 'default' };
    }
    if (now < validFrom) {
      return { text: '未生效', color: 'blue' };
    }
    if (now > validTo) {
      return { text: '已过期', color: 'red' };
    }
    if (coupon.maxUses !== null && coupon.currentUses >= coupon.maxUses) {
      return { text: '已用完', color: 'orange' };
    }
    return { text: '有效', color: 'green' };
  };

  const columns = [
    {
      title: '优惠券代码',
      dataIndex: 'code',
      key: 'code',
      render: (text: string) => <code className="bg-gray-100 px-2 py-1 rounded">{text}</code>
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '积分',
      dataIndex: 'credits',
      key: 'credits',
      render: (credits: number) => (
        <Tag color="gold">{credits} 积分</Tag>
      )
    },
    {
      title: '有效期',
      key: 'validity',
      render: (record: Coupon) => (
        <div className="text-xs">
          <div>{dayjs(record.validFrom).format('YYYY-MM-DD')}</div>
          <div>至 {dayjs(record.validTo).format('YYYY-MM-DD')}</div>
        </div>
      )
    },
    {
      title: '使用情况',
      key: 'usage',
      render: (record: Coupon) => (
        <div>
          <Tag color={record.currentUses > 0 ? 'blue' : 'default'}>
            {record.currentUses > 0 ? '已使用' : '未使用'}
          </Tag>
          <div className="text-xs text-gray-500 mt-1">
            {record.currentUses} / {record.maxUses === null ? '∞' : record.maxUses}
          </div>
        </div>
      )
    },
    {
      title: '状态',
      key: 'status',
      render: (record: Coupon) => {
        const status = getCouponStatus(record);
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '限制',
      key: 'restrictions',
      render: (record: Coupon) => (
        <Space size="small">
          {record.onlyForNewUsers && <Tag color="purple">仅新用户</Tag>}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'actions',
      render: (record: Coupon) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个优惠券吗？"
            description="已有使用记录的优惠券无法删除，只能停用"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 渲染优惠券管理内容
  const renderCouponManagement = () => (
    <div>
      {/* 头部设计 - 左右结构 */}
      <div className="w-full mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* 左侧：标题和统计 */}
          <div className="flex items-center gap-3">
            <GiftOutlined className="text-2xl text-cyan-600" />
            <h3 className="text-xl font-bold text-gray-800 mb-0">优惠券管理</h3>
            <Tag color="cyan">{pagination.total}</Tag>
          </div>
          
          {/* 右侧：操作按钮 */}
          <div className="flex flex-wrap gap-2">
            <Space>
              <Button
                type="default"
                icon={<PlusOutlined />}
                onClick={() => {
                  setBatchModalVisible(true);
                  batchForm.resetFields();
                  batchForm.setFieldsValue({
                    count: 10,
                    codePrefix: '',
                    codeLength: 8,
                    isActive: true,
                    onlyForNewUsers: false,
                    maxUses: null,
                    validFrom: dayjs(),
                    validTo: dayjs().add(1, 'year')
                  });
                  setBatchResult(null);
                }}
              >
                批量创建
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenModal()}
              >
                创建优惠券
              </Button>
            </Space>
          </div>
        </div>
      </div>

      {/* 筛选器 */}
      <Card className="mb-6 border border-gray-100" style={{
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }} styles={{ body: { padding: '20px 24px' } }}>
        <div className="flex flex-wrap gap-4 items-center justify-between">
          {/* 左侧：筛选器 */}
          <div className="flex flex-wrap gap-4 items-center">
            <Input
              placeholder="搜索优惠券代码或名称"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 300, borderRadius: '8px' }}
            />
            <Select
              value={usageStatus}
              onChange={(value) => {
                setUsageStatus(value);
                loadCoupons(1, searchText, value, isActiveFilter);
              }}
              style={{ width: 120, borderRadius: '8px' }}
            >
              <Option value="all">全部状态</Option>
              <Option value="used">已使用</Option>
              <Option value="unused">未使用</Option>
            </Select>
            <Select
              value={isActiveFilter}
              onChange={(value) => {
                setIsActiveFilter(value);
                loadCoupons(1, searchText, usageStatus, value);
              }}
              style={{ width: 120, borderRadius: '8px' }}
            >
              <Option value="all">全部</Option>
              <Option value="true">已启用</Option>
              <Option value="false">已停用</Option>
            </Select>
          </div>
          
          {/* 右侧：操作按钮 */}
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleSearch}
              type="primary"
              style={{ borderRadius: '20px' }}
            >
              搜索
            </Button>
            <Button 
              onClick={() => {
                setSearchText('');
                setUsageStatus('all');
                setIsActiveFilter('all');
                loadCoupons(1, '', 'all', 'all');
              }}
              style={{ borderRadius: '20px' }}
            >
              重置
            </Button>
          </div>
        </div>
      </Card>

      {/* 内容表格 */}
      <Card 
        className="border border-gray-100 mx-0"
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
        styles={{ body: { padding: '0' } }}
      >
        {/* 顶部装饰渐变线 */}
        <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1 w-full"></div>

        {coupons.length === 0 && !loading ? (
          <div style={{ padding: '60px 20px' }}>
            <Empty
              description={
                <div>
                  <Text className="text-gray-500 text-responsive-sm">暂无优惠券</Text>
                  <br />
                  <Text className="text-gray-400 text-xs mt-2">
                    点击上方按钮创建您的第一张优惠券
                  </Text>
                </div>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={coupons}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
              onChange: handleTableChange,
              style: { padding: '20px' }
            }}
            style={{ padding: '20px' }}
          />
        )}
      </Card>
    </div>
  );

  // 渲染积分包管理内容
  const renderPackageManagement = () => {
    const enabledPackages = packages.filter(p => p.enabled).length;
    const popularPackages = packages.filter(p => p.popular).length;
    
    return (
      <div className="space-y-4">
        {/* 统计信息 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-blue-600">{packages.length}</div>
            <div className="text-sm text-gray-600">总积分包</div>
          </Card>
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-green-600">{enabledPackages}</div>
            <div className="text-sm text-gray-600">已启用</div>
          </Card>
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-red-600">{popularPackages}</div>
            <div className="text-sm text-gray-600">推荐套餐</div>
          </Card>
        </div>

        <Card
          title={
            <span>
              <GiftOutlined className="mr-2" />
              积分包管理
            </span>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedPackage(null);
                packageForm.resetFields();
                setEditPackageModal(true);
              }}
            >
              添加积分包
            </Button>
          }
          className="shadow-md"
        >
          <Table
            dataSource={packages}
            columns={packageColumns}
            rowKey="id"
            pagination={false}
          />
        </Card>
      </div>
    );
  };

  // 渲染订阅计划管理内容
  const renderSubscriptionManagement = () => {
    const enabledPlans = plans.filter(p => p.enabled).length;
    const popularPlans = plans.filter(p => p.popular).length;
    
    return (
      <div className="space-y-4">
        {/* 统计信息 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-purple-600">{plans.length}</div>
            <div className="text-sm text-gray-600">总订阅计划</div>
          </Card>
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-green-600">{enabledPlans}</div>
            <div className="text-sm text-gray-600">已启用</div>
          </Card>
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-purple-600">{popularPlans}</div>
            <div className="text-sm text-gray-600">推荐计划</div>
          </Card>
        </div>

        <Card
          title={
            <span>
              <CreditCardOutlined className="mr-2" />
              订阅计划管理
            </span>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedPlan(null);
                planForm.resetFields();
                setEditPlanModal(true);
              }}
            >
              添加订阅计划
            </Button>
          }
          className="shadow-md"
        >
          <Table
            dataSource={plans}
            columns={planColumns}
            rowKey="id"
            pagination={false}
          />
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center space-x-3">
        <GiftOutlined className="text-2xl text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">营销管理</h2>
      </div>

      <Alert
        message="营销管理说明"
        description="管理优惠券、积分包和订阅计划。请谨慎操作，修改后将立即对用户生效。"
        type="warning"
        showIcon
        closable
      />

      {/* Tab切换 */}
      <Tabs 
        defaultActiveKey="coupons"
        size="large"
        items={[
              {
                key: 'coupons',
                label: (
                  <span className="flex items-center gap-2">
                    <GiftOutlined className="text-cyan-600" />
                    <span className="font-medium">优惠券管理</span>
                    <Tag color="cyan">{pagination.total}</Tag>
                  </span>
                ),
                children: renderCouponManagement()
              },
              {
                key: 'packages',
                label: (
                  <span className="flex items-center gap-2">
                    <GiftOutlined className="text-green-600" />
                    <span className="font-medium">积分包管理</span>
                    <Tag color="green">{packages.length}</Tag>
                  </span>
                ),
                children: renderPackageManagement()
              },
              {
                key: 'subscriptions',
                label: (
                  <span className="flex items-center gap-2">
                    <CrownOutlined className="text-purple-600" />
                    <span className="font-medium">订阅计划管理</span>
                    <Tag color="purple">{plans.length}</Tag>
                  </span>
                ),
                children: renderSubscriptionManagement()
              }
            ]}
          />

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingCoupon ? '编辑优惠券' : '创建优惠券'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="code"
            label="优惠券代码"
            rules={[
              { required: true, message: '请输入优惠券代码' },
              { pattern: /^[A-Z0-9]+$/, message: '只能包含大写字母和数字' }
            ]}
          >
            <Input placeholder="例如：WELCOME2024" disabled={!!editingCoupon} />
          </Form.Item>

          <Form.Item
            name="name"
            label="优惠券名称"
            rules={[{ required: true, message: '请输入优惠券名称' }]}
          >
            <Input placeholder="例如：新用户注册礼包" />
          </Form.Item>

          <Form.Item
            name="credits"
            label="赠送积分"
            rules={[
              { required: true, message: '请输入积分数量' },
              { type: 'number', min: 1, message: '积分必须大于0' }
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="优惠券描述信息" />
          </Form.Item>

          <Form.Item
            name="validFrom"
            label="生效时间"
            rules={[{ required: true, message: '请选择生效时间' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="validTo"
            label="过期时间"
            rules={[{ required: true, message: '请选择过期时间' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="maxUses"
            label="最大使用次数"
            tooltip="留空表示无限制"
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="留空表示无限制" />
          </Form.Item>

          <Form.Item
            name="onlyForNewUsers"
            label="仅限新用户"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情抽屉 */}
      <Drawer
        title="优惠券详情"
        placement="right"
        width={600}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedCoupon && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="优惠券代码">
                <code className="bg-gray-100 px-2 py-1 rounded">{selectedCoupon.code}</code>
              </Descriptions.Item>
              <Descriptions.Item label="名称">{selectedCoupon.name}</Descriptions.Item>
              <Descriptions.Item label="积分">
                <Tag color="gold">{selectedCoupon.credits} 积分</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="描述">{selectedCoupon.description || '-'}</Descriptions.Item>
              <Descriptions.Item label="生效时间">
                {dayjs(selectedCoupon.validFrom).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="过期时间">
                {dayjs(selectedCoupon.validTo).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="使用次数">
                {selectedCoupon.currentUses} / {selectedCoupon.maxUses === null ? '无限制' : selectedCoupon.maxUses}
              </Descriptions.Item>
              <Descriptions.Item label="仅限新用户">
                {selectedCoupon.onlyForNewUsers ? '是' : '否'}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={getCouponStatus(selectedCoupon).color}>
                  {getCouponStatus(selectedCoupon).text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(selectedCoupon.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            {selectedCoupon.usages && selectedCoupon.usages.length > 0 && (
              <>
                <Divider>使用记录</Divider>
                <Table
                  dataSource={selectedCoupon.usages}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '用户',
                      key: 'user',
                      render: (record) => {
                        const user = record.user;
                        return user ? (user.username || user.email || user.phone || user.id) : record.userId;
                      }
                    },
                    {
                      title: '使用时间',
                      dataIndex: 'usedAt',
                      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
                    }
                  ]}
                />
              </>
            )}
          </div>
        )}
      </Drawer>

      {/* 批量创建模态框 */}
      <Modal
        title="批量创建优惠券"
        open={batchModalVisible}
        onOk={handleBatchCreate}
        onCancel={() => {
          setBatchModalVisible(false);
          batchForm.resetFields();
          setBatchResult(null);
        }}
        width={600}
        okText="批量创建"
        cancelText="取消"
      >
        <Form
          form={batchForm}
          layout="vertical"
        >
          <Form.Item
            name="count"
            label="生成数量"
            rules={[
              { required: true, message: '请输入生成数量' },
              { type: 'number', min: 1, max: 1000, message: '数量必须在1-1000之间' }
            ]}
          >
            <InputNumber min={1} max={1000} style={{ width: '100%' }} placeholder="最多1000张" />
          </Form.Item>

          <Form.Item
            name="codePrefix"
            label="代码前缀（可选）"
            tooltip="例如：WELCOME，生成的代码将是 WELCOMEXXXX 格式"
          >
            <Input placeholder="留空则完全随机生成" />
          </Form.Item>

          <Form.Item
            name="codeLength"
            label="代码总长度"
            rules={[{ required: true, message: '请输入代码长度' }]}
            tooltip="包含前缀长度，建议8-12位"
          >
            <InputNumber min={6} max={20} style={{ width: '100%' }} defaultValue={8} />
          </Form.Item>

          <Form.Item
            name="name"
            label="优惠券名称"
            rules={[{ required: true, message: '请输入优惠券名称' }]}
            tooltip="批量创建时会在名称后自动添加序号"
          >
            <Input placeholder="例如：新用户礼包" />
          </Form.Item>

          <Form.Item
            name="credits"
            label="赠送积分"
            rules={[
              { required: true, message: '请输入积分数量' },
              { type: 'number', min: 1, message: '积分必须大于0' }
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={2} placeholder="优惠券描述信息" />
          </Form.Item>

          <Form.Item
            name="validFrom"
            label="生效时间"
            rules={[{ required: true, message: '请选择生效时间' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="validTo"
            label="过期时间"
            rules={[{ required: true, message: '请选择过期时间' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="maxUses"
            label="每张优惠券最大使用次数"
            tooltip="留空表示无限制，每张优惠券每人仅限使用一次"
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="留空表示无限制" />
          </Form.Item>

          <Form.Item
            name="onlyForNewUsers"
            label="仅限新用户"
            valuePropName="checked"
            tooltip="每张优惠券代码，每人仅限使用一次（已自动限制）"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>

        {batchResult && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <div className="font-semibold mb-2">创建结果：</div>
            <div>成功：{batchResult.codes?.length || 0} 张</div>
            <div>失败：{batchResult.failedCount || 0} 张</div>
            {batchResult.codes && batchResult.codes.length > 0 && (
              <div className="mt-2">
                <div className="font-semibold mb-1">生成的优惠券代码：</div>
                <div className="bg-white p-2 rounded max-h-32 overflow-y-auto">
                  {batchResult.codes.map((code: string, idx: number) => (
                    <code key={idx} className="block mb-1">{code}</code>
                  ))}
                </div>
                <Button
                  size="small"
                  onClick={() => {
                    const text = batchResult.codes.join('\n');
                    navigator.clipboard.writeText(text);
                    message.success('已复制到剪贴板');
                  }}
                  className="mt-2"
                >
                  复制所有代码
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 编辑积分包模态框 */}
      <Modal
        title={
          <span>
            <EditOutlined className="mr-2" />
            {selectedPackage ? '编辑积分包' : '添加积分包'}
          </span>
        }
        open={editPackageModal}
        onOk={() => packageForm.submit()}
        onCancel={() => setEditPackageModal(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={packageForm}
          layout="vertical"
          onFinish={handleSavePackage}
        >
          <Form.Item
            label="包名"
            name="name"
            rules={[{ required: true, message: '请输入包名' }]}
          >
            <Input placeholder="例如：标准包" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="积分数量"
              name="credits"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={999999} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="价格 (￥)"
              name="price"
              rules={[{ required: true }]}
            >
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item
            label="赠送积分"
            name="bonus"
          >
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="热门推荐"
              name="popular"
              valuePropName="checked"
              tooltip="开启后将在前台显示为推荐套餐"
            >
              <Switch checkedChildren="推荐" unCheckedChildren="普通" />
            </Form.Item>

            <Form.Item
              label="启用状态"
              name="enabled"
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* 编辑订阅计划模态框 */}
      <Modal
        title={
          <span>
            <EditOutlined className="mr-2" />
            {selectedPlan ? '编辑订阅计划' : '添加订阅计划'}
          </span>
        }
        open={editPlanModal}
        onOk={() => planForm.submit()}
        onCancel={() => setEditPlanModal(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={planForm}
          layout="vertical"
          onFinish={handleSavePlan}
        >
          <Form.Item
            label="计划名称"
            name="name"
            rules={[{ required: true }]}
          >
            <Input placeholder="例如：Pro会员" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="价格 (￥)"
              name="price"
              rules={[{ required: true }]}
            >
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="周期"
              name="period"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="monthly">每月</Option>
                <Option value="yearly">每年</Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            label="积分数量"
            name="credits"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={999999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="功能特权（每行一个）"
            name="features"
            rules={[{ required: true }]}
          >
            <TextArea
              rows={4}
              placeholder="每行输入一个特权功能&#10;例如：优先支持&#10;无广告体验"
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="推荐标记"
              name="popular"
              valuePropName="checked"
              tooltip="开启后将在前台显示为推荐套餐"
            >
              <Switch checkedChildren="推荐" unCheckedChildren="普通" />
            </Form.Item>

            <Form.Item
              label="启用状态"
              name="enabled"
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CouponManagement;

